import React, {useState, useEffect} from 'react';
import {
  StyleSheet,
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native-web';

import {useDispatch, useSelector} from 'react-redux';
import {setShowUnitSelectionScreen} from '../../../../store/course';
import {BASE_URL, TOKEN_GENERATION_API, API_KEY} from '@env';

const UnitSelectionOverlay = () => {
  const {width} = useWindowDimensions();
  // Main content container has horizontal padding: 20 on each side.
  const mainContentPadding = 20 * 2; // total 40px
  const availableWidth = width - mainContentPadding;
  // Fixed card (wrapper) width.
  const cardWrapperWidth = 320;
  // Desired gap (used between cards AND as outer spacing)
  const gap = 20;
  // Calculate the number of columns that can fit if we want outer gaps to equal inner gaps.
  // We use: availableWidth >= (numColumns * cardWrapperWidth) + ((numColumns + 1) * gap)
  // Solve: numColumns <= (availableWidth - gap) / (cardWrapperWidth + gap)
  const numColumns = Math.max(
    1,
    Math.floor((availableWidth - gap) / (cardWrapperWidth + gap)),
  );
  // Compute the exact grid width including outer gaps (left/right)
  const gridWidth = cardWrapperWidth * numColumns + gap * (numColumns + 1);

  // Build the grid style so that the left/right padding equals the gap and the inner column gap is also the gap.
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    columnGap: 10,
    rowGap: 60,
    paddingHorizontal: 20,
    marginTop: 40,
    marginBottom: 40,
    width: '100%',
  };

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const {courseData} = useSelector(state => state.course);
  const {loading} = useSelector(state => state.course);
  const [units, setUnits] = useState(null);
  const avatarUrl = useSelector(state => state.meeting.meeting.avatarUrlValue);

  const showUnitSelectionScreen = useSelector(
    state => state.course.showUnitSelectionScreen,
  );

  const courseTokenValues = useSelector(state => state.app.courseTokenValues);
  const dispatch = useDispatch();

  useEffect(() => {
    if (courseData && showUnitSelectionScreen) {
      setUnits(courseData.units);
    }
  }, [courseData, showUnitSelectionScreen]);

  if (!courseData || !units || !showUnitSelectionScreen) return;

  const handleClose = () => {
    setUnits(null);
    dispatch(setShowUnitSelectionScreen(false));
  };

  return (
    <ScrollView style={styles.container}>
      <TopBar onClose={handleClose} />
      <View style={styles.mainContent}>
        {errorMessage && (
          <Text style={styles.errorMessage}>{errorMessage}</Text>
        )}
        {units.length === 0 && (
          <Text style={styles.noUnits}>
            No units were found for this subject and grade.
          </Text>
        )}
        {/* If still loading, show the loading message; otherwise, display the units grid */}
        {loading ? (
          <Text style={styles.noUnits}>Fetching Units for you...</Text>
        ) : (
          <View style={[styles.unitsContainer, gridStyle]}>
            {units.map((unit, index) => (
              <View key={index} style={styles.unitCardWrapper}>
                <UnitCard
                  unit={unit}
                  courseTokenValues={courseTokenValues}
                  courseData={courseData}
                  avatarUrl={avatarUrl}
                />
              </View>
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );
};

// TopBar Component with a close icon
const TopBar = ({onClose}) => {
  return (
    <View style={styles.topBar}>
      <TouchableOpacity style={styles.closeIcon} onPress={onClose}>
        <Text style={styles.closeIconText}>✕</Text>
      </TouchableOpacity>
      <View style={styles.topBarInner}>
        <View style={styles.headerBox}>
          <Text style={styles.headerBoxText}>
            WHERE WOULD YOU LIKE TO START?
          </Text>
        </View>
        <Text style={styles.subHeaderText}>
          Teleport to a world to start your adventure
        </Text>
      </View>
    </View>
  );
};

// UnitCard Component
// UnitCard Component (synchronous)
const UnitCard = ({unit, courseTokenValues, courseData, avatarUrl}) => {
  // Asynchronous helper function for generating token
  const generateMetaverseToken = async (userId, curriculumId, unitId) => {
    const endpoint = `${BASE_URL}${TOKEN_GENERATION_API}`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY, // Replace with your actual API key
        },
        body: JSON.stringify({
          userId,
          curriculumId,
          unitId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate metaverse token');
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('Error generating metaverse token:', error);
      throw error;
    }
  };

  // Asynchronous event handler for teleport
  const handleTeleport = async () => {
    try {
      const userId = courseTokenValues.userId;
      const curriculumId = courseData.curriculumId; //i.e. updated curriculumId
      const unitId = unit.id;

      const token = await generateMetaverseToken(userId, curriculumId, unitId);
      window.location.href = `${window.location.origin}/?token=${token}&avatarSelection=false&legacy=false&avatarUrl=${avatarUrl}`;
    } catch (error) {
      console.error('Redirect failed due to token generation error:', error);
    }
  };

  return (
    <View style={styles.unitCard}>
      <View style={styles.card}>
        <View style={styles.cardImageContainer}>
          <Image
            source={{uri: unit.unitImage}}
            style={styles.cardImage}
            resizeMode="cover"
          />
        </View>
        <View style={styles.cardContent}>
          <View style={styles.cardHeader}>
            <Text style={styles.unitNumber}>Unit {unit.unitNumber}</Text>
            <Text style={styles.lessonCount}>
              {unit.numberOfLessons} Lessons
            </Text>
          </View>
          <Text style={styles.unitTitle} numberOfLines={2}>
            {unit.title}
          </Text>
          <ProgressBar
            label="Unit Mastery"
            percentage={unit.unitMasteryPercentage}
          />
          <TouchableOpacity
            onPress={handleTeleport}
            style={styles.teleportButton}>
            <View style={styles.teleportIcon} />
            <Text style={styles.teleportButtonText}>Teleport</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

// ProgressBar Component
const ProgressBar = ({label, percentage}) => {
  let gradient;
  if (percentage < 30) {
    gradient =
      'linear-gradient(to bottom, #FF3333 0%, #CC2626 45%, #B32424 55%)';
  } else if (percentage > 70) {
    gradient =
      'linear-gradient(to bottom, #BEE034 0%, #93AD28 45%, #8BA426 55%)';
  } else {
    gradient =
      'linear-gradient(to bottom, #F3F3F3 0%, #C7C7C7 45%, #B4B4B4 55%)';
  }

  return (
    <View style={styles.progressBarContainer}>
      <View style={styles.progressBarHeader}>
        <Text style={styles.progressLabel}>{label}</Text>
        <Text style={styles.progressPercentage}>{percentage}%</Text>
      </View>
      <View style={styles.progressBarOuter}>
        <View
          style={[
            styles.progressBarInner,
            {
              width: `${percentage}%`,
              ...(Platform.OS === 'web' && {backgroundImage: gradient}),
            },
          ]}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute', // Ensures full screen coverage
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999, // Ensures this component is on top
    backgroundColor: 'black',
  },
  mainContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  loadingOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    color: 'white',
    fontSize: 18,
  },
  topBar: {
    position: Platform.OS === 'web' ? 'sticky' : 'relative',
    top: 0,
    zIndex: 40,
    borderTopWidth: 2,
    borderBottomWidth: 2,
    borderColor: '#C5E836',
    backgroundColor: '#333',
    paddingVertical: 16,
    ...(Platform.OS === 'web' && {
      backgroundImage: "url('/intersecting-circles.png')",
      backgroundSize: '15px 15px',
      backgroundRepeat: 'repeat',
    }),
  },
  closeIcon: {
    position: 'absolute',
    right: 16,
    top: 16,
    padding: 8,
    zIndex: 50, // Extra zIndex to ensure it's clickable
  },
  closeIconText: {
    fontSize: 24,
    color: 'black',
    fontWeight: 'bold',
    backgroundColor: '#C5E836',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  topBarInner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerBox: {
    marginHorizontal: 24,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 8,
    ...(Platform.OS === 'web' && {
      backgroundImage:
        'linear-gradient(to bottom, #C4E736 0%, #ADCC2F 45%, #8BA426 55%)',
    }),
  },
  headerBoxText: {
    fontSize: 24,
    fontWeight: '800',
    color: 'black',
    textAlign: 'center',
  },
  subHeaderText: {
    marginHorizontal: 24,
    fontSize: 19.2,
    fontWeight: '600',
    color: 'white',
    textAlign: 'center',
    marginTop: 8,
  },
  errorMessage: {
    marginTop: 16,
    color: 'red',
    fontSize: 16,
  },
  noUnits: {
    marginTop: 16,
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
  },
  // The units container uses our computed gridStyle.
  unitsContainer: {
    marginTop: 8,
  },
  unitCardWrapper: {
    width: 350,
    alignItems: 'center',
  },
  unitCard: {
    width: 300,
  },
  card: {
    backgroundColor: '#444',
    borderRadius: 12,
    overflow: 'hidden',
  },
  cardImageContainer: {
    height: 250,
    backgroundColor: '#444',
  },
  cardImage: {
    width: '100%',
    height: 250,
  },
  cardContent: {
    padding: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 8,
  },
  unitNumber: {
    backgroundColor: '#C5E836',
    paddingHorizontal: 12,
    paddingVertical: 4,
    fontWeight: 'bold',
    color: 'black',
    borderRadius: 16,
  },
  lessonCount: {
    backgroundColor: 'white',
    paddingHorizontal: 12,
    paddingVertical: 4,
    fontWeight: 'bold',
    color: 'black',
    borderRadius: 16,
  },
  unitTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    height: 60,
    marginBottom: 8,
  },
  teleportButton: {
    marginTop: 16,
    width: '95%',
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#C5E836',
    paddingVertical: 8,
    borderRadius: 8,
  },
  teleportIcon: {
    marginRight: 4,
    width: '15%',
    height: '100%',
    ...(Platform.OS === 'web' && {
      backgroundImage: "url('/Teleport-Button.png')",
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
    }),
  },
  teleportButtonText: {
    fontWeight: 'bold',
    fontSize: 18,
    color: 'black',
  },
  progressBarContainer: {
    padding: 8,
  },
  progressBarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  progressLabel: {
    fontSize: 16,
    color: 'white',
  },
  progressPercentage: {
    fontSize: 16,
    color: 'white',
  },
  progressBarOuter: {
    height: 16,
    width: '100%',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'gray',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 0 3px rgba(102,102,102,1)',
    }),
  },
  progressBarInner: {
    height: '100%',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      transitionProperty: 'width',
      transitionDuration: '300ms',
    }),
  },
});

export default UnitSelectionOverlay;
